import { ErrorHandler, NgModule } from '@angular/core';
import { BrowserModule, HAMMER_GESTURE_CONFIG, HammerModule } from '@angular/platform-browser';
import { Router, RouteReuseStrategy } from '@angular/router';
import { IonicModule, IonicRouteStrategy } from '@ionic/angular';
import { IonicStorageModule } from '@ionic/storage';
import { SplashScreen } from '@ionic-native/splash-screen/ngx';
import { StatusBar } from '@ionic-native/status-bar/ngx';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
// routing
import { AppRoutingModule } from './app-routing.module';
// containers
import { AppComponent } from './app.component';
// services
import { RestaurantService } from './core/services/restaurant.service';
import { PaymentService } from './core/services/payment.service';
import { RouteService } from './core/services/route.service';
import { OrderApiService } from './core/services/order.api.service';
import { StorageService } from './core/services/storage.service';
import { SquarePayService } from './core/services/square-pay-service.service';
import { FavoriteProductService } from './core/services/favorite-product.service';
// stores
import { RestaurantStore } from './core/stores/restaurant.store';
import { CartStore } from './core/stores/cart.store';
import { MerchantStore } from './core/stores/merchant.store';
import { OrderStore } from './core/stores/order.store';
import { PaymentStore } from './core/stores/payment.store';
// interceptors
import { HttpErrorInterceptor } from './core/interceptors/http-error-interceptor';
// components
import { ExternalRedirectionComponent } from './core/components/external-redirection/external-redirection.component';
// ionic plugins
import { Device } from '@ionic-native/device/ngx';
import { Platform } from '@ionic/angular';
import { NativeAudio } from '@ionic-native/native-audio/ngx';
import { MerchantService } from './core/services/merchant.service';
import { OneSignal } from '@ionic-native/onesignal/ngx';
import { environment } from '../environments/environment';

import * as Sentry from '@sentry/angular';
import { Integrations } from '@sentry/tracing';
import { FormatDataService } from './core/services/format-data.service';
import { DatePipe, LowerCasePipe } from '@angular/common';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { MatSliderModule } from '@angular/material/slider';
import { GestureConfig } from '@angular/material/core';
import { SwipeGestureService } from './core/services/swipe-gesture.service';
import { SharedModule } from './shared/shared.module';
import { NgxIntlTelInputModule } from 'ngx-intl-tel-input';

//
import { Geolocation } from '@ionic-native/geolocation/ngx';
import { InAppBrowser } from '@ionic-native/in-app-browser/ngx';
import { Diagnostic } from '@ionic-native/diagnostic/ngx';
import { UtilService } from './core/services/util.service';
import { ProcessDataService } from './core/services/process-data.service';
import { SocketIoConfig, SocketIoModule } from 'ngx-socket-io';
import {TableRedirectService} from './core/services/TableRedirectService';
import {MatDialogModule} from '@angular/material/dialog';
import {ReactiveFormsModule} from '@angular/forms';
import {MatExpansionModule} from '@angular/material/expansion';
import {MatTooltipModule} from "@angular/material/tooltip";
import { OrderWithFlavorStore } from './core/stores/orderwithflavor.store';
import { CustomerStore } from './core/stores/customer.store';

Sentry.init({
  dsn:
    'https://1651f75dd13c4df9a0bf3e511e87b31d@o411299.ingest.sentry.io/5286297',
  environment: environment.production ? 'production' : 'staging',
  integrations: [
    new Integrations.BrowserTracing({
      routingInstrumentation: Sentry.routingInstrumentation,
    }),
  ],
  tracesSampleRate: 1.0,
});

const config: SocketIoConfig = { url: environment.wsUrl, options: {
    path: '/ws/socket.io',
    transports: ['websocket', 'polling']
  } };

@NgModule({
  declarations: [AppComponent, ExternalRedirectionComponent],
  imports: [
    BrowserModule,
    IonicModule.forRoot(),
    IonicStorageModule.forRoot(),
    AppRoutingModule,
    HttpClientModule,
    BrowserAnimationsModule,
    MatSliderModule,
    HammerModule,
    SharedModule,
    NgxIntlTelInputModule,
    MatDialogModule,
    ReactiveFormsModule,
    SocketIoModule.forRoot(config),
    MatExpansionModule,
    MatTooltipModule
  ],
  providers: [
    StatusBar,
    SplashScreen,
    // interceptors
    {
      provide: RouteReuseStrategy,
      useClass: IonicRouteStrategy,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpErrorInterceptor,
      multi: true,
    },
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler({
        showDialog: false,
      }),
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    {
      provide: HAMMER_GESTURE_CONFIG,
      useClass: GestureConfig,
    },
    // services
    RestaurantService,
    FormatDataService,
    MerchantService,
    RouteService,
    PaymentService,
    OrderApiService,
    StorageService,
    SwipeGestureService,
    UtilService,
    ProcessDataService,
    SquarePayService,
    FavoriteProductService,
    // stores
    RestaurantStore,
    MerchantStore,
    CartStore,
    OrderStore,
    PaymentStore,
    OrderWithFlavorStore,
    CustomerStore,
    // ionic plugins
    Device,
    Platform,
    NativeAudio,
    OneSignal,
    //
    Geolocation,
    InAppBrowser,
    Diagnostic,
    // pipes
    LowerCasePipe,
    DatePipe,
    TableRedirectService
  ],
  bootstrap: [AppComponent],
})
export class AppModule {
  constructor(trace: Sentry.TraceService) {}
}
